import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Skeleton — we never use spinners on data. Always skeleton over the layout
 * that the data will occupy, so the screen does not jolt when the load resolves.
 */
const Skeleton = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('skeleton', className)} aria-hidden {...props} />
  ),
)
Skeleton.displayName = 'Skeleton'

export { Skeleton }

/** Convenience: a row of stat-tile skeletons. */
export function SkeletonTiles({ count = 4 }: { count?: number }) {
  return (
    <div className={cn('grid gap-3', count === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3')}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-md border bg-card p-4 shadow-soft">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-7 w-32" />
          <Skeleton className="mt-2 h-3 w-16" />
        </div>
      ))}
    </div>
  )
}

/** Convenience: a table-row skeleton. */
export function SkeletonRows({ rows = 6, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-border">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="grid items-center gap-4 px-4 py-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }).map((__, c) => (
            <Skeleton key={c} className={cn('h-3', c === 0 ? 'w-3/4' : 'w-1/2')} />
          ))}
        </div>
      ))}
    </div>
  )
}
